import { AccountService } from '@account-users/account/services/account.service';
import { Injectable } from '@angular/core';
import { Effect, Actions, ofType } from '@ngrx/effects';
import { ToastrService } from 'ngx-toastr';
import { zip } from 'rxjs';
import { switchMap, catchError, map } from 'rxjs/operators';
import {
  LicensesActionTypes,
  FetchAllLicensesSuccess,
  FetchAllLicensesError,
  FetchAccountLicensesError,
  FetchLicensePackageSuccess,
  FetchLicensePackageError,
} from './licenses.actions';
import {
  LicenseCode,
  LicensePackage,
  LicensePackageCode,
} from '@account-users/account/models';

@Injectable()
export class LicensesEffects {
  @Effect()
  fetchAllAccountLicenses = this.actions$.pipe(
    ofType(LicensesActionTypes.FetchAllLicenses),
    switchMap(({ payload }) => {
      const { accountId, filters } = payload;
      return zip(
        this.accountService.getAllLicenses(),
        this.accountService.getAccountLicenses(accountId, filters),
      ).pipe(
        switchMap(([allLicenses, accountLicenses]) => {
          return [
            new FetchAllLicensesSuccess({ allLicenses, accountLicenses }),
          ];
        }),
        catchError(() => {
          this.toastr.error('Could not fetch licenses', 'Request failed');
          return [new FetchAllLicensesError(), new FetchAccountLicensesError()];
        }),
      );
    }),
  );

  @Effect()
  fetchLicensePackage = this.actions$.pipe(
    ofType(LicensesActionTypes.FetchLicensePackage),
    switchMap(() =>
      this.accountService.getLicensePackages().pipe(
        map((packages) => {
          const sortPackages = (pkgs: LicensePackage[]): LicensePackage[] => {
            const enumValues = Object.values(LicensePackageCode);
            return pkgs.sort((a, b) => {
              const indexA = enumValues.indexOf(a.code);
              const indexB = enumValues.indexOf(b.code);
              return indexA - indexB;
            });
          };
          const sortLicenses = (pkgs: LicenseCode[]): LicenseCode[] => {
            const enumValues = Object.values(LicenseCode);
            return pkgs.sort((a, b) => {
              const indexA = enumValues.indexOf(a);
              const indexB = enumValues.indexOf(b);
              return indexA - indexB;
            });
          };
          const sorted = sortPackages(packages);
          sorted.forEach((pkg) => {
            pkg.licenses = sortLicenses(pkg.licenses);
          });
          return new FetchLicensePackageSuccess({ packages: sorted });
        }),
        catchError(() => {
          this.toastr.error(
            'Could not fetch license packages',
            'Request failed',
          );
          return [new FetchLicensePackageError()];
        }),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private accountService: AccountService,
    private toastr: ToastrService,
  ) {}
}
